<template>
  <v-card :loading="loading" max-width="500">
    <v-card-title class="headline grey lighten-2" primary-title
      >{{ $t(`settings.notification.email.title`) }}
      <v-spacer />
      <v-btn icon @click="$emit('hide')"><v-icon>close</v-icon></v-btn>
    </v-card-title>

    <v-row align="center" class="mx-2">
      <v-col>
        <v-text-field
          v-model="emailValue"
          :label="$t('profile.email')"
          :rules="[rules.email]"
          :disabled="me && me.id != id && !role('safetyRepresentative')"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          :disabled="me && me.id != id && !role('safetyRepresentative')"
          color="primary"
          text
          @click="changeEmail"
          :loading="loading"
          >{{ $t("dialog.change") }}</v-btn
        >
      </v-col>
    </v-row>
    <v-alert
      v-if="!emailValue && !loading"
      dense
      outlined
      icon="error"
      type="error"
      class="white-alert safety-elevation-3 mx-4"
      >{{ $t(`settings.notification.email.noEmailAlert`) }}</v-alert
    >

    <v-divider />

    <template v-if="items">
      <v-subheader
        ><span>{{ $t(`settings.notification.email.topic.weekly`) }}</span>
        <v-spacer />
        <span class="text-end"
          >{{ $t(`settings.notification.email.nextTime`) }}
          {{ emailWeeklyNotificationNextTime | dateTime }}</span
        ></v-subheader
      >

      <notification-settings-card-list
        :items="weeklyItems"
        :disabled="
          loading ||
            (!role('safetyRepresentative') && me && me.id != id) ||
            !emailValue
        "
        @changeSubscription="changeSubscription"
      />

      <v-divider />
      <v-subheader v-if="eventBasedItems">{{
        $t(`settings.notification.email.topic.eventBased`)
      }}</v-subheader>

      <notification-settings-card-list
        :items="eventBasedItems"
        :disabled="
          loading ||
            (!role('safetyRepresentative') && me && me.id != id) ||
            !emailValue
        "
        @changeSubscription="changeSubscription"
      />
    </template>

    <v-divider />

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="grey" text @click="$emit('hide')">{{
        $t("dialog.cancel")
      }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import NotificationSettingsCardList from "@/components/notification/NotificationSettingsCardList.vue";
import EmailNotificationTopicsQuery from "@/graphql/queries/notification/EmailNotificationTopics.gql";
import EmailWeeklyNotificationNextTimeQuery from "@/graphql/queries/notification/EmailWeeklyNotificationNextTime.gql";

import ChangeEmailQuery from "@/graphql/mutation/profile/ChangeEmail.gql";
import AddSubscription from "@/graphql/mutation/profile/AddSubscription.gql";
import DeleteSubscription from "@/graphql/mutation/profile/DeleteSubscription.gql";
import { mapGetters, mapState } from "vuex";

export default {
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
    email: {
      type: [String, null],
    },
    subscriptions: {
      type: Array,
      required: true,
    },
  },
  components: {
    NotificationSettingsCardList,
  },

  data() {
    return {
      loading: false,
      subscriptionValue: true,
      emailValue: null,
      eventBasedTopics: [
        "mentorshipEndedAll",
        "mentorshipEndedMyMentee",
        "mentorshipFeedbackSubmitted",
        "riskAnalysisActionRequired",
      ],
      weeklyTopics: [
        "pendingActions",
        "externalTrainings",
        "departmentInfo",
        "subordinatesInfo",
        "weeklyStatistics",
      ],
      rules: {
        email: (value) => {
          if (!value) {
            return true;
          }
          return /\S+@\S+\.\S+/.test(value) || "email invalid";
        },
      },
    };
  },

  mounted() {
    this.emailValue = this.email;
  },

  watch: {
    email() {
      this.emailValue = this.email;
    },
  },
  computed: {
    ...mapState(["me"]),
    ...mapGetters(["role"]),
    items() {
      return (
        (this.emailNotificationTopics &&
          this.emailNotificationTopics.map((el) => {
            const subscription = this.subscriptions.find((s) => s.topic == el);
            return {
              topic: el,
              createdAt: subscription ? subscription.createdAt : undefined,
              hasSubscription: !!subscription,
            };
          })) ||
        []
      );
    },
    weeklyItems() {
      return this.items.filter((ent) => this.weeklyTopics.includes(ent.topic));
    },
    eventBasedItems() {
      return this.items.filter((ent) =>
        this.eventBasedTopics.includes(ent.topic)
      );
    },
  },

  methods: {
    changeEmail() {
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: ChangeEmailQuery,
          variables: {
            employeeId: this.id,
            email: this.emailValue,
          },
        })
        .then(() => {
          this.$emit("refresh");
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: err,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    changeSubscription(topic) {
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: this.subscriptions.find((s) => s.topic == topic)
            ? DeleteSubscription
            : AddSubscription,
          variables: {
            topic,
            employeeId: this.id,
          },
        })
        .then(() => {
          this.$emit("refresh");
        })
        .catch((err) => {
          this.$store.commit("push_global_message", {
            color: "error",
            text: err,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  apollo: {
    emailNotificationTopics: {
      query: EmailNotificationTopicsQuery,
    },
    emailWeeklyNotificationNextTime: {
      query: EmailWeeklyNotificationNextTimeQuery,
    },
  },
};
</script>
